import type { Permission } from '@/types/api';
import {
  MobileOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  SettingOutlined,
  UserOutlined,
  FileSearchOutlined,
  DashboardOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';

export interface RouteConfig {
  key: string;
  path: string;
  label: string;
  icon?: any;
  permission?: Permission;
  children?: RouteConfig[];
}

/**
 * 统一的路由配置
 * 包含路由路径、菜单信息、权限要求
 */
export const routeConfig: RouteConfig[] = [
  {
    key: '1',
    path: '/device-model',
    label: '机型管理',
    icon: MobileOutlined,
    permission: 'device:view',
  },
  {
    key: '2',
    path: '/device',
    label: '设备管理',
    icon: DatabaseOutlined,
    permission: 'device:view',
  },
  {
    key: 'ota',
    path: '/ota',
    label: 'OTA管理',
    icon: CloudUploadOutlined,
    children: [
      {
        key: '3',
        path: '/version',
        label: '版本管理',
        icon: FileTextOutlined,
        permission: 'version:view',
      },
      {
        key: '4',
        path: '/task',
        label: '进度与监控',
        icon: DashboardOutlined,
        permission: 'progress:monitor',
      },
      {
        key: '5',
        path: '/app-upgrade',
        label: 'App升级管理',
        icon: AppstoreOutlined,
        permission: 'version:view',
      },
    ],
  },
  {
    key: 'statistics',
    path: '/statistics',
    label: '数据统计',
    icon: PieChartOutlined,
    children: [
      {
        key: '6',
        path: '/upgrade-stats',
        label: '升级统计',
        icon: LineChartOutlined,
        permission: 'progress:monitor',
      },
      {
        key: '7',
        path: '/device-stats',
        label: '设备统计',
        icon: BarChartOutlined,
        permission: 'progress:monitor',
      },
      {
        key: '8',
        path: '/version-stats',
        label: '版本统计',
        icon: PieChartOutlined,
        permission: 'progress:monitor',
      },
    ],
  },
  {
    key: 'system',
    path: '/system',
    label: '系统管理',
    icon: SettingOutlined,
    children: [
      {
        key: '9',
        path: '/user',
        label: '用户管理',
        icon: UserOutlined,
        permission: 'user:manage',
      },
      {
        key: '10',
        path: '/system-log',
        label: '系统日志',
        icon: FileSearchOutlined,
        permission: 'audit:view',
      },
    ],
  },
];

/**
 * 构建路由到权限的映射
 */
export const buildRoutePermissions = (): Record<string, Permission> => {
  const map: Record<string, Permission> = {};
  
  const traverse = (routes: RouteConfig[]) => {
    routes.forEach(route => {
      if (route.permission && route.path) {
        map[route.path] = route.permission;
      }
      if (route.children) {
        traverse(route.children);
      }
    });
  };
  
  traverse(routeConfig);
  return map;
};

/**
 * 构建菜单 key 到权限的映射
 */
export const buildMenuPermissions = (): Record<string, Permission> => {
  const map: Record<string, Permission> = {};
  
  const traverse = (routes: RouteConfig[]) => {
    routes.forEach(route => {
      if (route.permission && route.key) {
        map[route.key] = route.permission;
      }
      if (route.children) {
        traverse(route.children);
      }
    });
  };
  
  traverse(routeConfig);
  return map;
};

/**
 * 构建路由到菜单 key 的映射
 */
export const buildPathToKey = (): Record<string, string> => {
  const map: Record<string, string> = {};
  
  const traverse = (routes: RouteConfig[]) => {
    routes.forEach(route => {
      if (route.path && route.key && !route.children) {
        map[route.path] = route.key;
      }
      if (route.children) {
        traverse(route.children);
      }
    });
  };
  
  traverse(routeConfig);
  return map;
};

/**
 * 构建菜单 key 到路由的映射
 */
export const buildKeyToPath = (): Record<string, string> => {
  const map: Record<string, string> = {};
  
  const traverse = (routes: RouteConfig[]) => {
    routes.forEach(route => {
      if (route.path && route.key && !route.children) {
        map[route.key] = route.path;
      }
      if (route.children) {
        traverse(route.children);
      }
    });
  };
  
  traverse(routeConfig);
  return map;
};
